import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const InputDropDown = (props) => {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const [searchText, setSearchText] = useState("");
	const dropdownRef = useRef(null);
	const placeholder = props.defaultName ? props.defaultName : "Select An Option";
	const selectedValue = props.value || placeholder;
	const isPlaceholder = selectedValue === placeholder;
	const options = props.options || [];
	const isSearchable = props.searchable || options.length > 12;
	const filteredOptions = isSearchable && searchText
		? options.filter((option) =>
				t(option).toLowerCase().includes(searchText.trim().toLowerCase())
			)
		: options;

	useEffect(() => {
		if (!isOpen) return undefined;

		const handleOutsideClick = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};

		const handleEscape = (event) => {
			if (event.key === "Escape") {
				setIsOpen(false);
			}
		};

		document.addEventListener("mousedown", handleOutsideClick);
		document.addEventListener("keydown", handleEscape);
		return () => {
			document.removeEventListener("mousedown", handleOutsideClick);
			document.removeEventListener("keydown", handleEscape);
		};
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) {
			setSearchText("");
		}
	}, [isOpen]);

	const commitValue = (nextValue) => {
		const event = {
			target: {
				id: props.name,
				name: props.name,
				type: "select-one",
				value: nextValue,
				required: props.required,
				validity: {
					valueMissing: props.required && !nextValue,
					typeMismatch: false,
				},
			},
		};

		props.onChange(event);
		if (props.onBlur) {
			setTimeout(() => props.onBlur(event), 0);
		}
		setIsOpen(false);
	};

	return (
		<div
			className="child-register-select-field"
			style={props.style}
			ref={dropdownRef}
		>
			{props.label ? (
				<p
					className="child-register-select-label"
				>
					{t(props.label)}
					{props.required ? <span style={{ color: "red" }}>*</span> : null}
				</p>
			) : null}
			<button
				type="button"
				id={props.name}
				className={`child-register-select-trigger${
					isPlaceholder ? " is-empty" : ""
				}`}
				disabled={props.disabled}
				onClick={() => setIsOpen((current) => !current)}
				aria-haspopup="listbox"
				aria-expanded={isOpen}
			>
				<span>{t(selectedValue)}</span>
				<span className="material-icons">expand_more</span>
			</button>
			{isOpen ? (
				<div className="child-register-select-menu" role="listbox">
					{isSearchable ? (
						<div className="child-register-select-search">
							<span className="material-icons">search</span>
							<input
								type="search"
								value={searchText}
								onChange={(event) => setSearchText(event.target.value)}
								placeholder={t("Search")}
								autoFocus
							/>
						</div>
					) : null}
					{filteredOptions?.length ? (
						filteredOptions.map((option) => {
							const isSelected = option === selectedValue;
							return (
								<button
									type="button"
									key={option}
									className={`child-register-select-option${
										isSelected ? " is-selected" : ""
									}`}
									onClick={() => commitValue(option)}
									role="option"
									aria-selected={isSelected}
								>
									<span>{t(option)}</span>
									{isSelected ? (
										<span className="material-icons">check</span>
									) : null}
								</button>
							);
						})
					) : (
						<div className="child-register-select-empty">
							{t(searchText ? "No matching options" : "No options available")}
						</div>
					)}
				</div>
			) : null}
			{props.error ? (
				<p className="red-text m-0">{`${
					t(props.defaultValue) || t("Select An Option")
				}`}</p>
			) : null}
		</div>
	);
};

export default InputDropDown;
